import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import { DocumentIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import {
  useIsPrepareDisclosuresWizardDisabled,
  useResolvedDisclosuresSendAction,
} from 'app/pages/loanApplication/hooks';
import { PrepareDisclosuresWizardTestIds } from 'app/pages/loanApplication/PrepareDisclosuresWizard/PrepareDisclosuresWizard.testIds';

import useLoanId from 'hooks/useLoanId';

import { openPrepareDisclosuresWizardAction } from 'store/loanApplications/actions';
import { selectUnderwritingExpirationDates } from 'store/loanApplications/selectors/underwriting.selectors';

import { ctaButtonStyles } from './PrepareDisclosuresCta.styles';
import { getDisclosureDueChip } from './PrepareDisclosuresCta.utils';

const PrepareDisclosuresCta = () => {
  const dispatch = useDispatch();

  const loanId = useLoanId();
  const expirationDates = useSelector(selectUnderwritingExpirationDates);

  const isWizardDisabled = useIsPrepareDisclosuresWizardDisabled();
  const { sendAction: sendFormBundleAction } = useResolvedDisclosuresSendAction(
    isWizardDisabled ? null : loanId,
  );

  const dueChip = useMemo(
    () => getDisclosureDueChip(expirationDates?.earliestClosingDate),
    [expirationDates],
  );

  const onOpenWizard = useCallback(() => {
    dispatch(openPrepareDisclosuresWizardAction());
  }, [dispatch]);

  if (isWizardDisabled || !sendFormBundleAction) {
    return null;
  }

  return (
    <Box className="flex items-center gap-8" data-testid={PrepareDisclosuresWizardTestIds.CTA}>
      {dueChip && (
        <Box data-testid={PrepareDisclosuresWizardTestIds.CTA_DUE_CHIP}>
          <LabelCDS size={LabelSize.S} title={dueChip.label} variant={dueChip.variant} />
        </Box>
      )}
      <Button
        color="primary"
        data-testid={PrepareDisclosuresWizardTestIds.CTA_BUTTON}
        startIcon={<DocumentIcon />}
        sx={ctaButtonStyles}
        variant="contained"
        onClick={onOpenWizard}
      >
        Prepare Disclosures
      </Button>
    </Box>
  );
};

export default memo(PrepareDisclosuresCta);
